import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Switch,
  TextField,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  useTheme,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkIcon from "@mui/icons-material/Work";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkflows } from "../../redux/Slice/Workflow/workflowSlice";
import ReactTimezoneSelect from "react-timezone-select";
import { buildTimezoneSelectStyles } from "./timezoneSelectStyles";
import axios from "axios";
import { getAuthToken } from "../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function timeToMinutes(t) {
  if (!t || typeof t !== "string") return null;
  const parts = t.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function minTimeStrings(arr) {
  const ok = (arr || []).filter(Boolean);
  if (!ok.length) return null;
  return ok.reduce((best, cur) => {
    const bm = timeToMinutes(best);
    const cm = timeToMinutes(cur);
    if (bm == null) return cur;
    if (cm == null) return best;
    return cm < bm ? cur : best;
  });
}

function maxTimeStrings(arr) {
  const ok = (arr || []).filter(Boolean);
  if (!ok.length) return null;
  return ok.reduce((best, cur) => {
    const bm = timeToMinutes(best);
    const cm = timeToMinutes(cur);
    if (bm == null) return cur;
    if (cm == null) return best;
    return cm > bm ? cur : best;
  });
}

/** Earliest start and latest end across slots (for summary / single-slot office fields). */
function spanFromSlots(slots) {
  const list = (slots || []).filter((s) => s?.start && s?.end);
  if (!list.length) return { officeStart: "09:00", officeEnd: "18:00" };
  return {
    officeStart: minTimeStrings(list.map((s) => s.start)),
    officeEnd: maxTimeStrings(list.map((s) => s.end)),
  };
}

/** True if two [start,end] ranges share any minute. Handles overnight ranges (start > end in minutes). */
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  const as = timeToMinutes(aStart);
  const ae = timeToMinutes(aEnd);
  const bs = timeToMinutes(bStart);
  const be = timeToMinutes(bEnd);
  if (as == null || ae == null || bs == null || be == null) return false;
  // Split overnight ranges into two same-day segments [start,1440] + [0,end]
  const aSegs = as <= ae ? [[as, ae]] : [[as, 1440], [0, ae]];
  const bSegs = bs <= be ? [[bs, be]] : [[bs, 1440], [0, be]];
  return aSegs.some(([a1, a2]) => bSegs.some(([b1, b2]) => Math.max(a1, b1) < Math.min(a2, b2)));
}

const OfficeTimeFlowConfig = ({ workflowId, onSave }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const { workflows: workflowGroups, selectedWorkflow } = useSelector(
    (state) => state.workflow
  );

  // Flatten workflows for dropdown
  const allWorkflows = Array.isArray(workflowGroups)
    ? workflowGroups.flatMap((group) => group.workflows || [])
    : [];

  // Initialize default days config
  const getInitialDaysConfig = () => {
    const initialConfig = {};
    DAYS.forEach((day) => {
      initialConfig[day] = {
        enabled: false,
        officeStart: "09:00",
        officeEnd: "18:00",
        slots: [],
      };
    });
    return initialConfig;
  };

  // State for configuration
  const [timezone, setTimezone] = useState("Asia/Karachi");
  const [daysConfig, setDaysConfig] = useState(getInitialDaysConfig());
  const [nonOfficeWorkflow, setNonOfficeWorkflow] = useState("");
  const [expandedDay, setExpandedDay] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Load existing configuration from workflow
  useEffect(() => {
    if (selectedWorkflow?.currentVersion?.branches?.start?.[0]?.trigger) {
      const trigger = selectedWorkflow.currentVersion.branches.start[0].trigger;
      if (trigger.name === "Office-Time Flow" && trigger.config) {
        const configData = Array.isArray(trigger.config)
          ? trigger.config.find((c) => c.timezone || c.officeHours) ||
            trigger.config[0]
          : trigger.config;

        if (typeof configData === "string") {
          try {
            const parsed = JSON.parse(configData);
            loadConfiguration(parsed);
          } catch (e) {
            console.error("Error parsing config:", e);
          }
        } else if (configData && (configData.timezone || configData.officeHours)) {
          loadConfiguration(configData);
        }
      }
    }
  }, [selectedWorkflow]);

  const validateDay = useCallback((day) => {
    const dayConfig = daysConfig[day];
    if (!dayConfig || !dayConfig.enabled) {
      setErrors((prev) => ({ ...prev, [day]: null }));
      return true;
    }

    const errors = [];

    if (dayConfig.slots.length === 0) {
      errors.push("At least one time slot is required when day is enabled");
    } else {
      const complete = dayConfig.slots.filter((s) => s?.start && s?.end);
      for (let i = 0; i < dayConfig.slots.length; i++) {
        const slot = dayConfig.slots[i];
        if (!slot.start || !slot.end) {
          errors.push(`Slot ${i + 1} must have both start and end times`);
        } else if (slot.start === slot.end) {
          errors.push(`Slot ${i + 1} start and end time cannot be the same`);
        }
        if (!slot.workflowId) {
          errors.push(`Slot ${i + 1} must have a workflow selected`);
        }
      }

      const origIdx = (slot) => dayConfig.slots.indexOf(slot);
      for (let i = 0; i < complete.length; i++) {
        for (let j = i + 1; j < complete.length; j++) {
          if (
            rangesOverlap(
              complete[i].start,
              complete[i].end,
              complete[j].start,
              complete[j].end
            )
          ) {
            const a = origIdx(complete[i]) + 1;
            const b = origIdx(complete[j]) + 1;
            errors.push(
              `Slots ${a} and ${b} overlap; adjust times or merge ranges`
            );
          }
        }
      }

      const hasOvernightSlot = complete.some((s) => {
        const sm = timeToMinutes(s.start);
        const em = timeToMinutes(s.end);
        return sm != null && em != null && sm > em;
      });
      const span = spanFromSlots(dayConfig.slots);
      if (
        !hasOvernightSlot &&
        complete.length > 0 &&
        timeToMinutes(span.officeStart) != null &&
        timeToMinutes(span.officeEnd) != null &&
        timeToMinutes(span.officeStart) >= timeToMinutes(span.officeEnd)
      ) {
        errors.push("Earliest slot start must be before latest slot end");
      }
    }

    setErrors((prev) => ({
      ...prev,
      [day]: errors.length > 0 ? errors : null,
    }));

    return errors.length === 0;
  }, [daysConfig]);

  useEffect(() => {
    if (expandedDay) {
      validateDay(expandedDay);
    }
  }, [daysConfig, expandedDay, validateDay]);
  

  const loadConfiguration = (config) => {
    if (config.timezone) {
      const tzValue = typeof config.timezone === 'string' ? config.timezone : config.timezone.value || 'UTC';
      setTimezone(tzValue);
    }
    if (config.nonOfficeWorkflow) {
      const workflowId =
        typeof config.nonOfficeWorkflow === "string"
          ? config.nonOfficeWorkflow
          : config.nonOfficeWorkflow.workflowId;
      if (workflowId) {
        setNonOfficeWorkflow(workflowId);
      }
    }

    const newDaysConfig = {};
    DAYS.forEach((day) => {
      const daySlots = config.officeHours?.[day] || [];
      if (daySlots.length > 0) {
        const mappedSlots = daySlots.map((slot) => ({
          start: slot.start,
          end: slot.end,
          workflowId: slot.workflowId,
          workflowName: slot.workflowName || "",
        }));
        const { officeStart, officeEnd } = spanFromSlots(mappedSlots);
        newDaysConfig[day] = {
          enabled: true,
          officeStart,
          officeEnd,
          slots: mappedSlots,
        };
      } else {
        newDaysConfig[day] = {
          enabled: false,
          officeStart: "09:00",
          officeEnd: "18:00",
          slots: [],
        };
      }
    });
    setDaysConfig(newDaysConfig);
  };

  // Fetch workflows on mount
  useEffect(() => {
    if (allWorkflows.length === 0) {
      dispatch(fetchWorkflows());
    }
  }, [dispatch, allWorkflows.length]);

  const handleDayToggle = (day) => {
    setDaysConfig((prev) => {
      const wasEnabled = prev[day]?.enabled;
  
      if (wasEnabled) {
        // Disable day
        return {
          ...prev,
          [day]: {
            ...prev[day],
            enabled: false,
            slots: [],
          },
        };
      }
  
      // Enable day → create first slot automatically
      return {
        ...prev,
        [day]: {
          enabled: true,
          officeStart: prev[day]?.officeStart || "09:00",
          officeEnd: prev[day]?.officeEnd || "18:00",
          slots: [
            {
              start: prev[day]?.officeStart || "09:00",
              end: prev[day]?.officeEnd || "18:00",
              workflowId: "",
              workflowName: "",
            },
          ],
        },
      };
    });
  
    setExpandedDay(day);
  };
  

  const handleOfficeTimeChange = (day, field, value) => {
    setDaysConfig((prev) => {
      const currentDayConfig = prev[day] || { enabled: false, officeStart: "09:00", officeEnd: "18:00", slots: [] };
      const updatedConfig = {
        ...currentDayConfig,
        [field]: value,
      };

      if (updatedConfig.slots?.length === 1) {
        updatedConfig.slots = updatedConfig.slots.map((slot, index) => {
          if (index !== 0) return slot;
          if (field === "officeStart") return { ...slot, start: value };
          if (field === "officeEnd") return { ...slot, end: value };
          return slot;
        });
      }

      const span = spanFromSlots(updatedConfig.slots);
      updatedConfig.officeStart = span.officeStart;
      updatedConfig.officeEnd = span.officeEnd;

      return {
        ...prev,
        [day]: updatedConfig,
      };
    });
  };

  const handleAddSlot = (day) => {
    setDaysConfig((prev) => {
      const currentDayConfig = prev[day] || { enabled: false, officeStart: "09:00", officeEnd: "18:00", slots: [] };
      const existingSlots = currentDayConfig.slots || [];
      const newSlot = {
        start: "",
        end: "",
        workflowId: "",
        workflowName: "",
      };
      const nextSlots = [...existingSlots, newSlot];
      const span = spanFromSlots(nextSlots);
      return {
        ...prev,
        [day]: {
          ...currentDayConfig,
          officeStart: span.officeStart,
          officeEnd: span.officeEnd,
          slots: nextSlots,
        },
      };
    });
  };

  const handleSlotChange = (day, slotIndex, field, value) => {
    setDaysConfig((prev) => {
      const currentDayConfig = prev[day] || { enabled: false, officeStart: "09:00", officeEnd: "18:00", slots: [] };
      const newSlots = [...(currentDayConfig.slots || [])];

      if (!newSlots[slotIndex]) {
        newSlots[slotIndex] = {
          start: "",
          end: "",
          workflowId: "",
          workflowName: "",
        };
      }

      newSlots[slotIndex] = {
        ...newSlots[slotIndex],
        [field]: value,
      };

      const span = spanFromSlots(newSlots);

      return {
        ...prev,
        [day]: {
          ...currentDayConfig,
          officeStart: span.officeStart,
          officeEnd: span.officeEnd,
          slots: newSlots,
        },
      };
    });
  };

  const handleDeleteSlot = (day, slotIndex) => {
    setDaysConfig((prev) => {
      const currentDayConfig = prev[day];
      const newSlots = currentDayConfig.slots.filter((_, i) => i !== slotIndex);
      const span = spanFromSlots(newSlots);

      return {
        ...prev,
        [day]: {
          ...currentDayConfig,
          officeStart: span.officeStart,
          officeEnd: span.officeEnd,
          slots: newSlots,
        },
      };
    });
  };
  

  const handleWorkflowSelect = (day, slotIndex, workflowId) => {
    const workflow = allWorkflows.find((w) => w._id === workflowId);
    handleSlotChange(day, slotIndex, "workflowId", workflowId);
    handleSlotChange(
      day,
      slotIndex,
      "workflowName",
      workflow ? workflow.name || workflow.currentVersion?.name : ""
    );
  };

  const validateAll = () => {
    let isValid = true;
    const newErrors = {};
  
    DAYS.forEach((day) => {
      const dayConfig = daysConfig[day];
      if (!dayConfig || !dayConfig.enabled) {
        newErrors[day] = null;
        return;
      }
  
      const errors = [];

      if (!dayConfig.slots || dayConfig.slots.length === 0) {
        errors.push("At least one time slot is required when day is enabled");
      } else {
        const complete = dayConfig.slots.filter((s) => s?.start && s?.end);
        for (let i = 0; i < dayConfig.slots.length; i++) {
          const slot = dayConfig.slots[i];
          if (!slot.start || !slot.end) {
            errors.push(`Slot ${i + 1} must have both start and end times`);
          } else if (slot.start === slot.end) {
            errors.push(`Slot ${i + 1} start and end time cannot be the same`);
          }
          if (!slot.workflowId) {
            errors.push(`Slot ${i + 1} must have a workflow selected`);
          }
        }

        const origIdx = (slot) => dayConfig.slots.indexOf(slot);
        for (let i = 0; i < complete.length; i++) {
          for (let j = i + 1; j < complete.length; j++) {
            if (
              rangesOverlap(
                complete[i].start,
                complete[i].end,
                complete[j].start,
                complete[j].end
              )
            ) {
              const a = origIdx(complete[i]) + 1;
              const b = origIdx(complete[j]) + 1;
              errors.push(`Slots ${a} and ${b} overlap; adjust times or merge ranges`);
            }
          }
        }

        const hasOvernightSlot = complete.some((s) => {
          const sm = timeToMinutes(s.start);
          const em = timeToMinutes(s.end);
          return sm != null && em != null && sm > em;
        });
        const span = spanFromSlots(dayConfig.slots);
        if (
          !hasOvernightSlot &&
          complete.length > 0 &&
          timeToMinutes(span.officeStart) != null &&
          timeToMinutes(span.officeEnd) != null &&
          timeToMinutes(span.officeStart) >= timeToMinutes(span.officeEnd)
        ) {
          errors.push("Earliest slot start must be before latest slot end");
        }
      }
  
      newErrors[day] = errors.length > 0 ? errors : null;
      if (errors.length > 0) isValid = false;
    });
  
    // Non-office workflow check
    if (!nonOfficeWorkflow) {
      newErrors.nonOffice = "Non-office workflow is required";
      isValid = false;
    } else {
      newErrors.nonOffice = null;
    }
  
    setErrors(newErrors);
    return isValid;
  };
  

  const handleSaveDay = (day) => {
    
  };

  const handleSave = async () => {
    if (!validateAll()) {
      console.error("Validation failed. Please fix errors before saving.");
      return;
    }

    setSaving(true);

    try {
      // Build configuration object
      const timezoneValue = typeof timezone === 'string' ? timezone : (timezone?.value || timezone || 'UTC');
      
      const config = {
        timezone: timezoneValue,
        officeHours: {},
        nonOfficeWorkflow: {
          workflowId: nonOfficeWorkflow,
          workflowName:
            allWorkflows.find((w) => w._id === nonOfficeWorkflow)?.name ||
            allWorkflows.find((w) => w._id === nonOfficeWorkflow)
              ?.currentVersion?.name ||
            "",
        },
      };

      // Build office hours for each day
      DAYS.forEach((day) => {
        const dayConfig = daysConfig[day];
        if (dayConfig && dayConfig.enabled && dayConfig.slots && dayConfig.slots.length > 0) {
          config.officeHours[day] = dayConfig.slots
            .filter(slot => slot && slot.start && slot.end && slot.workflowId) // Filter out incomplete slots
            .map((slot) => ({
              start: slot.start || dayConfig.officeStart || "09:00",
              end: slot.end || dayConfig.officeEnd || "18:00",
              workflowId: slot.workflowId,
              workflowName: slot.workflowName || allWorkflows.find((w) => w._id === slot.workflowId)?.name || "",
            }));
        }
      });

      console.log("Saving Office-Time Flow config:", config);

      // If onSave callback is provided, use it (for integration with ChooseTriggerDrawer)
      if (onSave) {
        await onSave(config);
      } else {
        // Standalone save (if component is used independently)
        if (selectedWorkflow && selectedWorkflow._id) {
          const token = await getAuthToken();
          if (!token) {
            throw new Error("No authentication token found");
          }

          // Update workflow trigger config
          const updatedWorkflow = { ...selectedWorkflow };
          const startBranch = updatedWorkflow.currentVersion.branches.start || [];
          
          if (startBranch.length > 0 && startBranch[0].trigger) {
            // Update existing trigger config
            const triggerConfig = Array.isArray(startBranch[0].trigger.config)
              ? startBranch[0].trigger.config
              : [];
            
            // Find or create config entry
            const configIndex = triggerConfig.findIndex(
              (c) => c.timezone || c.officeHours
            );
            
            if (configIndex >= 0) {
              triggerConfig[configIndex] = config;
            } else {
              triggerConfig.push(config);
            }
            
            startBranch[0].trigger.config = triggerConfig;
          } else {
            // Create new trigger node
            startBranch[0] = {
              id: Date.now().toString(),
              type: "trigger",
              trigger: {
                name: "Office-Time Flow",
                description: "Routes messages based on office hours and time slots",
                triggerType: "root",
                category: "Message",
                config: [config],
              },
            };
          }

          updatedWorkflow.currentVersion.branches.start = startBranch;

          // Save to backend
          await axios.put(
            `${SERVER_DOMAIN}/api/workflow/update/${selectedWorkflow._id}`,
            updatedWorkflow,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
        }
      }
    } catch (error) {
      console.error("Error saving Office-Time Flow configuration:", error);
      setErrors((prev) => ({
        ...prev,
        save: error.message || "Failed to save configuration",
      }));
    } finally {
      setSaving(false);
    }
  };

  const getPreviewData = () => {
    const preview = {
      officeHours: {},
      nonOffice: nonOfficeWorkflow
        ? allWorkflows.find((w) => w._id === nonOfficeWorkflow)?.name ||
          allWorkflows.find((w) => w._id === nonOfficeWorkflow)
            ?.currentVersion?.name ||
          "Non-Office Workflow"
        : null,
    };

    DAYS.forEach((day) => {
      const dayConfig = daysConfig[day];
      if (dayConfig && dayConfig.enabled && dayConfig.slots && dayConfig.slots.length > 0) {
        preview.officeHours[day] = dayConfig.slots.map((slot) => ({
          time: `${slot.start} - ${slot.end}`,
          workflow:
            allWorkflows.find((w) => w._id === slot.workflowId)?.name ||
            allWorkflows.find((w) => w._id === slot.workflowId)
              ?.currentVersion?.name ||
            "Unknown Workflow",
        }));
      }
    });

    return preview;
  };

  return (
    <Box
  sx={{
    p: 3,
    bgcolor: isDark ? "#181818" : "#f5f5f5",
    minHeight: "100%",
  }}
>
  <Typography variant="h5" fontWeight={700} mb={3}>
    Office-Time Flow Configuration
  </Typography>

  {/* Timezone Selector */}
  <Paper
    sx={{
      p: 2,
      mb: 3,
      bgcolor: isDark ? "#23272b" : "#fff",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      <AccessTimeIcon sx={{ mr: 1, color: "primary.main" }} />
      <Typography variant="h6" fontWeight={600}>
        Timezone
      </Typography>
    </Box>
    <ReactTimezoneSelect
      value={timezone}
      onChange={setTimezone}
      styles={buildTimezoneSelectStyles(isDark)}
    />
  </Paper>

  {/* Non-Office Workflow */}
  <Paper
    sx={{
      p: 2,
      mb: 3,
      bgcolor: isDark ? "#23272b" : "#fff",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      <WorkIcon sx={{ mr: 1, color: "primary.main" }} />
      <Typography variant="h6" fontWeight={600}>
        Non-Office Hours Workflow
      </Typography>
    </Box>
    <FormControl fullWidth>
      <InputLabel>Select Workflow</InputLabel>
      <Select
        value={nonOfficeWorkflow}
        onChange={(e) => setNonOfficeWorkflow(e.target.value)}
        label="Select Workflow"
        sx={{
          bgcolor: isDark ? "#23272b" : "#fff",
          color: isDark ? "#fff" : "#222",
        }}
      >
        {allWorkflows.map((workflow) => (
          <MenuItem key={workflow._id} value={workflow._id}>
            {workflow.name || workflow.currentVersion?.name || workflow._id}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    {errors.nonOffice && (
      <Alert severity="error" sx={{ mt: 1 }}>
        {errors.nonOffice}
      </Alert>
    )}
  </Paper>

  {/* Day Configuration */}
  <Typography variant="h6" fontWeight={600} mb={2}>
    Day Configuration
  </Typography>

  {DAYS.map((day) => {
    const dayData = daysConfig[day] || {};

    return (
      <Accordion
        key={day}
        expanded={expandedDay === day}
        onChange={(e, isExpanded) =>
          setExpandedDay(isExpanded ? day : null)
        }
        sx={{
          mb: 2,
          bgcolor: isDark ? "#23272b" : "#fff",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Switch
              checked={dayData.enabled || false}
              onChange={() => handleDayToggle(day)}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ ml: 2, flex: 1 }}
            >
              {day}
            </Typography>
            {dayData.enabled && (
              <Chip
                label={
                  (dayData.slots || []).filter((s) => s?.start && s?.end).length > 1
                    ? (dayData.slots || [])
                        .filter((s) => s?.start && s?.end)
                        .map((s) => `${s.start}–${s.end}`)
                        .join(" · ")
                    : `${dayData.officeStart} - ${dayData.officeEnd}`
                }
                size="small"
                sx={{ mr: 2, maxWidth: { xs: "100%", sm: 360 } }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {dayData.enabled ? (
            <Box>
              {/* Office hours: one span when a single slot; multiple disjoint windows when 2+ slots */}
              {(dayData.slots || []).length === 1 ? (
                <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                  <TextField
                    label="Office Start"
                    type="time"
                    value={dayData.officeStart || "09:00"}
                    onChange={(e) =>
                      handleOfficeTimeChange(day, "officeStart", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                  <TextField
                    label="Office End"
                    type="time"
                    value={dayData.officeEnd || "18:00"}
                    onChange={(e) =>
                      handleOfficeTimeChange(day, "officeEnd", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                </Box>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Add one row per time you are open (for example 12:00 AM–4:59 AM and 11:01 AM–11:59 PM when closed
                    5:00–11:00 AM). You do not add a row for the closed middle—the non-office workflow runs there
                    automatically.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Earliest start–latest end (summary): {dayData.officeStart} – {dayData.officeEnd}
                  </Typography>
                </Box>
              )}

              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Time ranges (slots)
              </Typography>

              {(dayData.slots || []).map((slot, slotIndex) => (
  <Paper
    key={slotIndex}
    sx={{
      p: 2,
      mb: 2,
      bgcolor: isDark ? "#181818" : "#f5f5f5",
    }}
  >
    <Stack spacing={1}>
      <TextField
        label="Start Time"
        type="time"
        value={slot.start || ""}
        onChange={(e) =>
          handleSlotChange(day, slotIndex, "start", e.target.value)
        }
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
        required
      />
      <TextField
        label="End Time"
        type="time"
        value={slot.end || ""}
        onChange={(e) =>
          handleSlotChange(day, slotIndex, "end", e.target.value)
        }
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
        required
      />
      <FormControl size="small" fullWidth>
        <InputLabel>Workflow</InputLabel>
        <Select
          value={slot.workflowId || ""}
          onChange={(e) =>
            handleWorkflowSelect(day, slotIndex, e.target.value)
          }
          label="Workflow"
        >
          {allWorkflows.map((workflow) => (
            <MenuItem key={workflow._id} value={workflow._id}>
              {workflow.name || workflow.currentVersion?.name || workflow._id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton
          onClick={() => handleDeleteSlot(day, slotIndex)}
          color="error"
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Stack>
  </Paper>
))}


              <Button
                startIcon={<AddIcon />}
                onClick={() => handleAddSlot(day)}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              >
                Add Slot
              </Button>

              {errors[day] && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {Array.isArray(errors[day]) ? (
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {errors[day].map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  ) : (
                    errors[day]
                  )}
                </Alert>
              )}

              <Button
                startIcon={<SaveIcon />}
                onClick={() => handleSaveDay(day)}
                variant="contained"
                size="small"
                sx={{ mt: 2 }}
              >
                Save Day
              </Button>
            </Box>
          ) : (
            <Typography color="text.secondary">
              Enable this day to configure office hours and time slots.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  })}

  {/* Preview */}
  <Paper
    sx={{
      p: 2,
      mt: 3,
      bgcolor: isDark ? "#23272b" : "#fff",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      <ScheduleIcon sx={{ mr: 1, color: "primary.main" }} />
      <Typography variant="h6" fontWeight={600}>
        Preview
      </Typography>
    </Box>
    <OfficeTimeFlowPreview data={getPreviewData()} isDark={isDark} />
  </Paper>

  {/* Save Button */}
  <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
    <Button
      variant="contained"
      color="primary"
      startIcon={<SaveIcon />}
      onClick={handleSave}
      disabled={saving}
      size="large"
    >
      {saving ? "Saving..." : "Save Configuration"}
    </Button>
  </Box>

  {errors.save && (
    <Alert severity="error" sx={{ mt: 2 }}>
      {errors.save}
    </Alert>
  )}
</Box>

  );
};

// Preview Component
const OfficeTimeFlowPreview = ({ data, isDark }) => {
  return (
    <Box>
      {/* Non-Office */}
      {data.nonOffice && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Non-Office Hours
          </Typography>
          <Chip label={data.nonOffice} color="secondary" />
        </Box>
      )}

      {/* Office Hours by Day */}
      <Typography variant="subtitle2" fontWeight={600} mb={2}>
        Office Hours
      </Typography>
      {Object.keys(data.officeHours).length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No office hours configured
        </Typography>
      ) : (
        Object.entries(data.officeHours).map(([day, slots]) => (
          <Box key={day} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} mb={1}>
              {day}
            </Typography>
            {slots.map((slot, idx) => (
              <Box
                key={idx}
                sx={{
                  ml: 2,
                  mb: 1,
                  p: 1,
                  bgcolor: isDark ? "#181818" : "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                  {slot.time}
                </Typography>
                <Chip label={slot.workflow} size="small" />
              </Box>
            ))}
          </Box>
        ))
      )}
    </Box>
  );
};

export default OfficeTimeFlowConfig;
